import React from "react";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import PropTypes from "prop-types";
import styled from "styled-components";
import {
  TextField,
  Button,
  Icon,
  BREAKPOINTS,
  COLORS
} from "@onelogin/react-components";
import { NEW_NOTE_SCREEN, SHOW_NOTE_SCREEN } from "../../constants/Env";

const messages = defineMessages({
  readOnly: {
    id: "readOnly",
    defaultMessage: "Read-only"
  }
});

const SecureNoteFooter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: ${props =>
    props.screen === NEW_NOTE_SCREEN ? "flex-end" : "space-between"};
  background-color: ${COLORS.WHITE};
  border-top: solid 1px ${COLORS.SEPARATOR_COLORS.MEDIUM};
  padding: 16px 24px;

  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    border: 0px;
    justify-content: initial;
    padding: 0px;
    flex-direction: column;
    background-color: transparent;
  }
`;

const SharedByBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  text-align: left;
  margin-right: 25px;

  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    flex-direction: row;

    justify-content: initial;
    width: 100%;

    margin-right: 0px;
    line-height: 24px;
    padding: 8px 0px;
    border-top: solid 1px ${COLORS.SEPARATOR_COLORS.MEDIUM};
  }
`;

const SharedByTextField = styled(TextField)`
  line-height: 16px;
  font-size: 13px;
  color: ${COLORS.GRAY_COLORS.GRAY_45};

  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    padding-right: 10px;
    padding-left: 20px;
    line-height: 24px;
    font-size: 14px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;

  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    width: 100%;
    border: 1px solid ${COLORS.GRAY_COLORS.GRAY_94};
    background-color: ${COLORS.GRAY_COLORS.GRAY_98};
    display: flex;
    align-items: center;
    justify-content: space-around;

    border: none;
    border-top: 1px solid ${COLORS.GRAY_COLORS.GRAY_94};
    padding: 16px 0px;
  }
`;

const StyledButton = styled.div`
  margin-right: 12px;
  flex-grow: 1;

  &:first-child {
    margin-left: 20px;
  }

  &:last-child {
    margin-right: 0px;
  }

  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    &:last-child {
      margin-right: 20px;
    }
  }
`;

const ShowNoteButton = styled(Button)`
  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    width: 100%;
  }
`;

const OwnerTextField = styled(TextField)`
  line-height: 22px;
  font-size: 14px;
  color: ${COLORS.GRAY_COLORS.GRAY_25};

  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    line-height: 24px;
  }
`;

const ReadOnlyBlock = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100px;
`;

const ReadOnlyTextField = styled(TextField)`
  display: flex;
  align-items: center;
  letter-spacing: 0.5px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;

  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    justify-content: center;
  }
`;

const ShowNoteFooter = ({
  currentNote,
  hasWriteAccess,
  goBackToNotesList,
  screen,
  onSaveNote,
  saveButtonDisabled
}) => {
  const intl = useIntl();
  return (
    <SecureNoteFooter screen={screen}>
      {currentNote && (
        <SharedByBlock>
          <SharedByTextField>
            <FormattedMessage id={"owned"} defaultMessage="Owned by" />
          </SharedByTextField>
          <OwnerTextField>
            {`${currentNote.owner.firstname} ${currentNote.owner.lastname}`}
          </OwnerTextField>
        </SharedByBlock>
      )}
      <ButtonGroup>
        {(hasWriteAccess || !currentNote) && (
          <>
            <StyledButton>
              <ShowNoteButton
                data-testid="cancel-button"
                look="regular"
                onClick={goBackToNotesList}
              >
                <FormattedMessage defaultMessage="Cancel" id="cancel" />
              </ShowNoteButton>
            </StyledButton>
            <StyledButton>
              <ShowNoteButton
                data-testid="save-button"
                look="primary"
                onClick={onSaveNote}
                disabled={saveButtonDisabled}
              >
                <FormattedMessage defaultMessage="Save" id="save" />
              </ShowNoteButton>
            </StyledButton>
          </>
        )}
        {screen === SHOW_NOTE_SCREEN && !hasWriteAccess && (
          <ReadOnlyBlock>
            <IconWrapper>
              <Icon
                name="Eye-24"
                size="24px"
                alt={intl.formatMessage(messages.readOnly)}
              />
            </IconWrapper>
            <ReadOnlyTextField data-testid="read-only-label">
              <FormattedMessage id={"readOnly"} defaultMessage="Read-only" />
            </ReadOnlyTextField>
          </ReadOnlyBlock>
        )}
      </ButtonGroup>
    </SecureNoteFooter>
  );
};

ShowNoteFooter.propTypes = {
  currentNote: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired
  }),
  goBackToNotesList: PropTypes.func.isRequired,
  screen: PropTypes.string.isRequired,
  onSaveNote: PropTypes.func.isRequired
};

export default ShowNoteFooter;
